import React, { useEffect, useMemo, useRef, useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Flex } from 'src/components/flex';
import Dialog from 'src/components/dialog/Dialog';
import LabelChip, { PROPERTY_LABEL_CHIP_COLOR, labelColorToCss } from 'src/components/labels/LabelChip';
import { type ILabel, LabelType, resolveLabels } from 'src/services/api/labels';

// colorless lead labels keep the default green; property labels fall back to grey
const chipColor = (label: ILabel) =>
    labelColorToCss(label.color) ||
    (label.type === LabelType.PROPERTY ? PROPERTY_LABEL_CHIP_COLOR : undefined);

export type EditableItem = {
    id: string | number;
    name: string;
    /**
     * Only the ids are read: each label is resolved against ``allLabels``, since
     * the copy embedded on a lead or property goes stale when a label is renamed.
     */
    labels: ILabel[];
};

type Props = {
    open: boolean;
    onClose: () => void;
    title: string;
    items: EditableItem[];
    allLabels: ILabel[];
    warningText: string;
    isSaving: boolean;
    onSave: (updatedItems: EditableItem[]) => Promise<void>;
};

export default function BulkEditLabelsModal({
    open,
    onClose,
    title,
    items: rawItems,
    allLabels,
    warningText,
    isSaving,
    onSave,
}: Props) {
    const items = useMemo(() => {
        const labelsById = new Map(allLabels.map((label) => [label.id, label]));
        return rawItems.map((item) => ({ ...item, labels: resolveLabels(item.labels, labelsById) }));
    }, [rawItems, allLabels]);

    const [itemStates, setItemStates] = useState<EditableItem[]>(items);
    const [hasTouched, setHasTouched] = useState(false);
    const itemsRef = useRef(items);
    itemsRef.current = items;

    useEffect(() => {
        if (!open) {
            return;
        }

        setItemStates(itemsRef.current);
        setHasTouched(false);
    }, [open]);

    const value = useMemo(() => {
        return allLabels.filter((label) =>
            itemStates.some((item) => item.labels.some((l) => l.id === label.id))
        );
    }, [allLabels, itemStates]);

    const handleLabelToggle = (label: ILabel) => {
        const isAdding = !value.some((v) => v.id === label.id);
        setHasTouched(true);

        if (isAdding) {
            setItemStates((prev) => prev.map((item) => {
                if (!item.labels.some((l) => l.id === label.id)) {
                    return { ...item, labels: [...item.labels, label] };
                }
                return item;
            }));
        } else {
            setItemStates((prev) => prev.map((item) => ({
                ...item,
                labels: item.labels.filter((l) => l.id !== label.id),
            })));
        }
    };

    const handleRemoveTag = (label: ILabel) => {
        setHasTouched(true);
        setItemStates((prev) => prev.map((item) => ({
            ...item,
            labels: item.labels.filter((l) => l.id !== label.id),
        })));
    };

    const save = async () => {
        await onSave(itemStates);
    };

    const oneItemSelected = items.length === 1;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: '100%' } }}
        >
            <DialogTitle>{title}</DialogTitle>

            <DialogContent>
                {items.length > 1 && (
                    <Accordion elevation={0} defaultExpanded sx={{ mb: 4 }}>
                        <AccordionSummary expandIcon={<KeyboardArrowDownIcon />} sx={{ px: 0 }}>
                            Current Labels
                        </AccordionSummary>

                        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 0 }}>
                            {itemStates.map((item) => (
                                <Flex key={item.id} alignItems="flex-start" columnGap={1} rowGap={0}>
                                    <Typography
                                        variant="body2"
                                        sx={{ minWidth: 140, maxWidth: 140, fontWeight: 500, pt: 0.25, wordBreak: 'break-word' }}
                                    >
                                        {item.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ pt: 0.25, color: 'text.secondary' }}>—</Typography>
                                    <Flex sx={{ flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
                                        {item.labels.length ? item.labels.map((label) => (
                                            <LabelChip key={label.id} name={label.name} color={chipColor(label)} size="body" />
                                        )) : (
                                            <Typography variant="body2" sx={{ color: 'text.disabled' }}>No labels</Typography>
                                        )}
                                    </Flex>
                                </Flex>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                )}

                <Autocomplete
                    multiple
                    options={allLabels}
                    value={value}
                    onChange={(_, __, reason) => {
                        if (reason === 'clear') {
                            setHasTouched(true);
                            setItemStates((prev) => prev.map((item) => ({ ...item, labels: [] })));
                        }
                    }}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Search labels"
                            variant="standard"
                            autoFocus
                        />
                    )}
                    renderOption={(_props, option) => {
                        const someItemsHaveLabel = itemStates.some((item) =>
                            item.labels.some((l) => l.id === option.id)
                        );
                        const someItemsDoNotHaveLabel = itemStates.some((item) =>
                            !item.labels.some((l) => l.id === option.id)
                        );
                        const selected = value.some((v) => v.id === option.id);

                        return (
                            <MenuItem
                                key={option.id}
                                onClick={() => handleLabelToggle(option)}
                                sx={{ bgcolor: selected ? 'grey.200' : 'transparent' }}
                            >
                                {!oneItemSelected && (
                                    <Checkbox
                                        checked={someItemsHaveLabel}
                                        indeterminate={someItemsHaveLabel && someItemsDoNotHaveLabel}
                                        disabled
                                        sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                                    />
                                )}
                                <LabelChip name={option.name} color={chipColor(option)} size="body" />
                            </MenuItem>
                        );
                    }}
                    renderTags={(tags, getTagProps) => tags.map((option, index) => {
                        const { onDelete: _, ...tagProps } = getTagProps({ index });

                        return (
                            <div {...tagProps} key={option.id}>
                                <LabelChip
                                    name={option.name}
                                    color={chipColor(option)}
                                    onDelete={() => handleRemoveTag(option)}
                                    size="body"
                                />
                            </div>
                        );
                    })}
                    sx={{
                        '& .MuiChip-root': { height: '24px' },
                        '& .MuiSvgIcon-root.MuiChip-deleteIcon': { mx: 0.5 },
                    }}
                />

                <DialogContentText sx={{ color: 'red', fontSize: 'small', mt: 2 }}>
                    {warningText}
                </DialogContentText>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isSaving}
                >
                    Cancel
                </Button>

                <LoadingButton
                    variant="contained"
                    onClick={save}
                    disabled={!hasTouched}
                    loading={isSaving}
                >
                    Save
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
